import React, { Suspense, lazy } from 'react';
import '@/App.css';
import '@/admin/admin.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { Toaster } from 'sonner';

// ============================================================================
// EAGER IMPORTS (in main bundle)
// ----------------------------------------------------------------------------
// These are loaded on every visit — splitting them would add a Suspense
// fallback to the very first render, hurting time-to-first-paint.
// ============================================================================
import Onboarding from '@/screens/Onboarding';
import LoginSender from '@/screens/LoginSender';
// Phase V3 — Recipient app retired: family members are WhatsApp-only.
// Phase V3.9 — Local Agent app retired: agents operate via WhatsApp commands.
import WelcomeSetup from '@/screens/WelcomeSetup';
import FirstRunDisclosure from '@/screens/FirstRunDisclosure';
import PinSetup from '@/screens/PinSetup';
import PinUnlock from '@/screens/PinUnlock';
import AdminReset from '@/screens/AdminReset';
import InstallPrompt from '@/components/InstallPrompt';
import AppReviewDemoBadge from '@/components/AppReviewDemoBadge';
import { AdminProtected } from '@/admin/AdminShell';

// ============================================================================
// LAZY-LOADED ROUTE CHUNKS
// ----------------------------------------------------------------------------
// Each role gets its own webpack chunk. A sender never downloads agent or
// admin screens; an agent never downloads admin screens; etc. Initial JS
// payload drops from ~228 KB gzipped to ~60-90 KB.
//
// /* webpackChunkName */ magic comments group multiple lazy imports into
// a single named chunk so we don't end up with one .js per screen.
// ============================================================================

// ---- Sender role bundle (v1 — minimal Apple-safe MVP) ----
const SenderHome              = lazy(() => import(/* webpackChunkName: "sender" */ '@/screens/sender/Home'));
const SenderFamily            = lazy(() => import(/* webpackChunkName: "sender" */ '@/screens/sender/Family'));
const SenderFamilyAdd         = lazy(() => import(/* webpackChunkName: "sender" */ '@/screens/sender/FamilyAdd'));
const SenderFamilyDetail      = lazy(() => import(/* webpackChunkName: "sender" */ '@/screens/sender/FamilyDetail'));
const SenderSupport           = lazy(() => import(/* webpackChunkName: "sender" */ '@/screens/sender/Support'));
const SenderActivity          = lazy(() => import(/* webpackChunkName: "sender" */ '@/screens/sender/Activity'));
const SenderProfile           = lazy(() => import(/* webpackChunkName: "sender" */ '@/screens/sender/Profile'));

// ---- Recipient role bundle: REMOVED in Phase V3 ----
// Family members no longer have an app; they interact through Nia only.
// All `/recipient/*` routes and the `recipient` user role have been
// retired. The admin "Recipients" page remains because it is the admin's
// read-only view of family_members data, not the recipient app.

// ---- Removed for v1 (deferred to v2) ----
// • Premium membership tier (Upgrade / Success / Cancel)
// • Coordinated planning helper (deferred)
// • Sender↔Agent chat (Chat / Inbox)
// • WhatsApp-inbound request approval (Requests)
// • Local Agent app — retired in Phase V3.9 (admin can still manage via /admin/agents)

// ---- Legal / help bundle ----
const LegalPrivacy            = lazy(() => import(/* webpackChunkName: "legal" */ '@/screens/legal/Privacy'));
const LegalTerms              = lazy(() => import(/* webpackChunkName: "legal" */ '@/screens/legal/Terms'));
const LegalRefund             = lazy(() => import(/* webpackChunkName: "legal" */ '@/screens/legal/Refund'));
const LegalRiskDisclosure     = lazy(() => import(/* webpackChunkName: "legal" */ '@/screens/legal/RiskDisclosure'));
const HelpScreen              = lazy(() => import(/* webpackChunkName: "legal" */ '@/screens/Help'));
const AboutNia                = lazy(() => import(/* webpackChunkName: "legal" */ '@/screens/AboutNia'));

// ---- Admin console bundle ----
const AdminOverview           = lazy(() => import(/* webpackChunkName: "admin" */ '@/admin/Overview'));
const AdminAnalytics          = lazy(() => import(/* webpackChunkName: "admin" */ '@/admin/Analytics'));
const AdminUsers              = lazy(() => import(/* webpackChunkName: "admin" */ '@/admin/Users'));
const AdminRecipients         = lazy(() => import(/* webpackChunkName: "admin" */ '@/admin/Recipients'));
const AdminReceiptsPage       = lazy(() => import(/* webpackChunkName: "admin" */ '@/admin/Receipts'));
const AdminNotificationsScreen= lazy(() => import(/* webpackChunkName: "admin" */ '@/admin/Notifications'));
// Super-admin URLs (not in sidebar, reachable by direct nav).
const AdminBoardKpis          = lazy(() => import(/* webpackChunkName: "admin" */ '@/admin/BoardKpis'));
const AdminAuditLogs          = lazy(() => import(/* webpackChunkName: "admin" */ '@/admin/AuditLogs'));
const AdminSettingsPage       = lazy(() => import(/* webpackChunkName: "admin" */ '@/admin/Settings'));

// ============================================================================
// Shared loading fallback rendered by Suspense while a chunk is being
// fetched. Brand-matched, identical to the existing Protected loading state
// so users don't perceive a jarring transition.
// ============================================================================
function RouteLoadingFallback() {
    return (
        <div className="nia-page" data-testid="route-loading-fallback">
            <div className="mobile-shell" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100dvh' }}>
                <div className="font-display" style={{ fontWeight: 900, fontSize: 24, color: 'var(--nia-primary)' }}>Nia</div>
            </div>
        </div>
    );
}

function roleHome(role) {
    if (role === 'sender') return '/sender/home';
    // Phase V3 — Recipients are WhatsApp-only; no app route to return.
    // Phase V3.9 — Local Agents are WhatsApp-only; no app route to return.
    // Anything else (or no role) bounces to the marketing root so they can
    // install / sign-in as a sender if they ever choose to.
    return '/';
}

function Protected({ role, children }) {
    const { user, loading, pinGate } = useAuth();
    const loc = useLocation();
    if (loading) {
        return <RouteLoadingFallback />;
    }
    if (!user) return <Navigate to="/" replace state={{ from: loc.pathname }} />;
    // Phase 17 — gate everything behind PIN unlock / setup when applicable.
    if (pinGate === 'unlock' && loc.pathname !== '/pin/unlock') {
        return <Navigate to="/pin/unlock" replace state={{ from: loc.pathname }} />;
    }
    if (pinGate === 'setup' && loc.pathname !== '/pin/setup') {
        return <Navigate to="/pin/setup" replace state={{ from: loc.pathname }} />;
    }
    if (role && user.role !== role) {
        return <Navigate to={roleHome(user.role)} replace />;
    }
    // Phase V3.9 — agents are WhatsApp-only; the agent in-app profile
    // gate was retired alongside /agent/*. Recipients are also WhatsApp-only.
    if (user.onboarded === false && loc.pathname !== '/setup') {
        return <Navigate to="/setup" replace />;
    }
    return children;
}

function SetupRoute() {
    const { user, loading } = useAuth();
    if (loading) return null;
    if (!user) return <Navigate to="/" replace />;
    if (user.onboarded) {
        return <Navigate to={roleHome(user.role)} replace />;
    }
    return <WelcomeSetup />;
}

// Phase 17 — PIN gate route guards. Only render the PIN screens when the
// gate is actually engaged, otherwise bounce back so deep-linking doesn't
// leak the PIN UI for users who haven't enrolled.
function PinUnlockRoute() {
    const { user, loading, pinGate } = useAuth();
    if (loading) return null;
    // The unlock gate is allowed even when `user` is null — that's the
    // "cold start, no JWT, but device has a PIN" path. The PinUnlock
    // component falls back to `pinIdentity` for the greeting + user_id.
    if (pinGate !== 'unlock') {
        if (!user) return <Navigate to="/" replace />;
        return <Navigate to={roleHome(user.role)} replace />;
    }
    return <PinUnlock />;
}

function PinSetupRoute() {
    const { user, loading, pinGate } = useAuth();
    if (loading) return null;
    if (!user) return <Navigate to="/" replace />;
    if (pinGate !== 'setup') return <Navigate to={roleHome(user.role)} replace />;
    return <PinSetup />;
}

function Router() {
    const { user, loading, pinGate } = useAuth();
    // Phase 17: when the PIN gate is engaged for a logged-in user, the root
    // "/" route should land on the appropriate PIN screen instead of bouncing
    // straight to the role home.
    //
    // Phase 24 update: the unlock gate can engage even WITHOUT a logged-in
    // user (returning visitor whose JWT expired but whose device PIN is
    // still valid). In that case we still want "/" → /pin/unlock so the
    // app re-authenticates via PIN rather than dumping the user back to
    // the phone/email OTP screen.
    const rootDestination = () => {
        if (loading) return null;
        if (pinGate === 'unlock') return <Navigate to="/pin/unlock" replace />;
        if (!user) return <Onboarding />;
        if (pinGate === 'setup') return <Navigate to="/pin/setup" replace />;
        if (user.onboarded === false && user.role !== 'agent') return <Navigate to="/setup" replace />;
        return <Navigate to={roleHome(user.role)} replace />;
    };
    return (
        // A single Suspense boundary at the route level. While a lazy chunk
        // is being fetched (rarely — only first time a user enters a section),
        // we show the same Nia loading screen as the auth guard, so the UX
        // is consistent and not jarring.
        <Suspense fallback={<RouteLoadingFallback />}>
            <Routes>
                <Route path="/" element={rootDestination()} />
                <Route path="/login/sender" element={<LoginSender />} />
                {/* /login/recipient retired in Phase V3 — family members are WhatsApp-only */}
                {/* /login/agent retired in Phase V3.9 — agents are WhatsApp-only */}
                <Route path="/setup" element={<SetupRoute />} />
                <Route path="/welcome" element={<Protected><FirstRunDisclosure /></Protected>} />
                <Route path="/pin/unlock" element={<PinUnlockRoute />} />
                <Route path="/pin/setup" element={<PinSetupRoute />} />

                {/* Legacy v2 surfaces — REMOVED for v1 (Apple-safe MVP).
                    All legacy routes redirect to home so existing bookmarks
                    or push-notification deep-links keep working. */}
                <Route path="/membership/upgrade" element={<Navigate to="/sender/home" replace />} />
                <Route path="/membership/success" element={<Navigate to="/sender/home" replace />} />
                <Route path="/membership/cancel" element={<Navigate to="/sender/home" replace />} />
                <Route path="/messages" element={<Navigate to="/sender/home" replace />} />
                <Route path="/chat/:threadId" element={<Navigate to="/sender/home" replace />} />

                {/* Legal & help (publicly accessible) */}
                <Route path="/legal/privacy" element={<LegalPrivacy />} />
                <Route path="/legal/terms" element={<LegalTerms />} />
                <Route path="/legal/refund" element={<LegalRefund />} />
                <Route path="/legal/risk" element={<LegalRiskDisclosure />} />
                <Route path="/help" element={<HelpScreen />} />
                <Route path="/about" element={<AboutNia />} />

                {/* Sender role */}
                <Route path="/sender/home" element={<Protected role="sender"><SenderHome /></Protected>} />
                <Route path="/sender/family" element={<Protected role="sender"><SenderFamily /></Protected>} />
                <Route path="/sender/family/add" element={<Protected role="sender"><SenderFamilyAdd /></Protected>} />
                <Route path="/sender/family/:id" element={<Protected role="sender"><SenderFamilyDetail /></Protected>} />
                <Route path="/sender/support" element={<Protected role="sender"><SenderSupport /></Protected>} />
                {/* Legacy route — keep, redirects to family-add flow. */}
                <Route path="/sender/support/new" element={<Protected role="sender"><Navigate to="/sender/family/add" replace /></Protected>} />
                <Route path="/sender/activity" element={<Protected role="sender"><SenderActivity /></Protected>} />
                <Route path="/sender/profile" element={<Protected role="sender"><SenderProfile /></Protected>} />

                {/* V1 back-compat redirects — these features are deferred to v2.
                    Sender bookmarks resolve to home. banned-terms:ok */}
                <Route path="/sender/bridge" element={<Navigate to="/sender/home" replace />} /> {/* banned-terms:ok */}
                <Route path="/sender/community" element={<Navigate to="/sender/home" replace />} />
                <Route path="/sender/community/new" element={<Navigate to="/sender/home" replace />} />
                <Route path="/sender/community/:id" element={<Navigate to="/sender/home" replace />} />
                <Route path="/sender/requests" element={<Navigate to="/sender/home" replace />} />

                {/* Recipient role — RETIRED in Phase V3. */}
                {/* Local Agent portal — RETIRED in Phase V3.9. */}

                <Route path="/admin/reset" element={<AdminReset />} />

                {/* Admin console */}
                <Route path="/admin" element={<AdminProtected><AdminOverview /></AdminProtected>} />
                <Route path="/admin/overview" element={<AdminProtected><AdminOverview /></AdminProtected>} />
                <Route path="/admin/analytics" element={<AdminProtected><AdminAnalytics /></AdminProtected>} />
                <Route path="/admin/users" element={<AdminProtected><AdminUsers /></AdminProtected>} />
                <Route path="/admin/family" element={<AdminProtected><AdminRecipients /></AdminProtected>} />
                <Route path="/admin/receipts" element={<AdminProtected><AdminReceiptsPage /></AdminProtected>} />
                <Route path="/admin/notifications" element={<AdminProtected><AdminNotificationsScreen /></AdminProtected>} />

                {/* Super-admin tools — reachable by URL only, not in sidebar. */}
                <Route path="/admin/board" element={<AdminProtected><AdminBoardKpis /></AdminProtected>} />
                <Route path="/admin/audit" element={<AdminProtected><AdminAuditLogs /></AdminProtected>} />
                <Route path="/admin/settings" element={<AdminProtected><AdminSettingsPage /></AdminProtected>} />

                {/* V1 back-compat redirects for removed admin sections. banned-terms:ok */}
                <Route path="/admin/requests" element={<Navigate to="/admin/overview" replace />} />
                <Route path="/admin/payouts" element={<Navigate to="/admin/overview" replace />} />
                <Route path="/admin/repayments" element={<Navigate to="/admin/overview" replace />} /* banned-terms:ok legacy redirect */ />
                <Route path="/admin/risk" element={<Navigate to="/admin/overview" replace />} />
                <Route path="/admin/agents" element={<Navigate to="/admin/overview" replace />} />
                <Route path="/admin/recipients" element={<Navigate to="/admin/family" replace />} />
                <Route path="/admin/community" element={<Navigate to="/admin/overview" replace />} />

                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </Suspense>
    );
}

export default function App() {
    return (
        <div className="App">
            <AuthProvider>
                <BrowserRouter>
                    <Router />
                    <InstallPrompt />
                    <AppReviewDemoBadge />
                    <Toaster position="top-center" richColors closeButton theme="light" />
                </BrowserRouter>
            </AuthProvider>
        </div>
    );
}
